import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'

import { DataTable, optionalColumn, selectionColumn } from '@/components/ui/data-table'
import type { ListSort } from '@/services/core/paging'
import { DateCell, MutedCell, NameCell, StatusChip } from '@/components/TableCells'
import type { RoleDetail } from '@/services/users/rolesApi'

/** The API field each sortable column orders by when the server sorts the list. */
const SORT_FIELDS: Record<string, string> = {
  name: 'name',
  application: 'application',
  scope: 'scope',
  objectState: 'objectState',
  updateTime: 'updateTime',
  createTime: 'createTime',
}

interface RoleTableProps {
  roles: RoleDetail[]
  loading?: boolean
  emptyMessage?: string
  selection?: RoleDetail[]
  onSelectionChange?: (roles: RoleDetail[]) => void
  /** Sorts on the server — pass both for a list the server pages. */
  sort?: ListSort | null
  onSortChange?: (sort: ListSort | null) => void
}

export function RoleTable({
  roles,
  loading,
  emptyMessage,
  selection,
  onSelectionChange,
  sort,
  onSortChange,
}: RoleTableProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const selectable = !!onSelectionChange

  const columns: ColumnDef<RoleDetail, unknown>[] = [
    ...(selectable ? [selectionColumn<RoleDetail>()] : []),
    {
      accessorKey: 'name',
      header: t('common.name'),
      meta: { minWidth: 200 },
      cell: ({ row }) => <NameCell name={row.original.name} id={row.original.id} />,
    },
    {
      accessorKey: 'application',
      header: t('roles.application'),
      size: 130,
      cell: ({ row }) => (
        <MutedCell
          value={
            row.original.application
              ? t(`userRoles.apps.${row.original.application}`, row.original.application)
              : null
          }
        />
      ),
    },
    {
      accessorKey: 'scope',
      header: t('roles.scope'),
      size: 110,
      cell: ({ row }) => (
        <MutedCell
          value={
            row.original.scope ? t(`roles.scopes.${row.original.scope}`, row.original.scope) : null
          }
        />
      ),
    },
    {
      accessorKey: 'description',
      header: t('common.description'),
      enableSorting: false,
      meta: { minWidth: 220 },
      cell: ({ row }) => <MutedCell value={row.original.description} />,
    },
    {
      id: 'permissions',
      header: t('roles.permissions'),
      enableSorting: false,
      size: 110,
      cell: ({ row }) => <MutedCell value={row.original.permissions.length} />,
    },
    {
      accessorKey: 'objectState',
      header: t('common.status'),
      size: 110,
      cell: ({ row }) => <StatusChip state={row.original.objectState} />,
    },
    optionalColumn<RoleDetail>({
      accessorKey: 'updateTime',
      header: t('common.updated'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.updateTime} />,
    }),
    optionalColumn<RoleDetail>({
      accessorKey: 'createTime',
      header: t('common.created'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.createTime} />,
    }),
  ]

  return (
    <DataTable
      columns={columns}
      data={roles}
      loading={loading}
      emptyMessage={emptyMessage ?? t('roles.noRolesFound')}
      columnsKey="roles"
      onRowClick={(row) => navigate(`/admin/roles/${row.id}`)}
      {...(selectable ? { selection, onSelectionChange } : {})}
      serverSort={
        onSortChange && { sort: sort ?? null, onChange: onSortChange, fields: SORT_FIELDS }
      }
    />
  )
}
